import { SKILL_IDS } from "../constants.ts";
import type { Fact, SkillId, StrategyDefinition } from "../model.ts";
import { FACT_MASTERY_STAGES } from "../mastery/fact-mastery.ts";
import { instantiateStrategyLesson, type LessonVariant, type StrategyLessonInstance } from "../lessons/lesson-engine.ts";
import type { SessionBucket } from "../sessions/session-machine.ts";
import type { SelectionDecision, SelectorInput } from "./select-next.ts";
import { factSchedulingBlock, SCHEDULING_POLICY, type SchedulingState } from "./scheduling.ts";

export interface PlanningInput extends SelectorInput {
  readonly scheduling: SchedulingState;
}
export type PlanningResult = {
  readonly kind: "ITEM";
  readonly decision: SelectionDecision;
  readonly bucket: SessionBucket;
  readonly lesson?: StrategyLessonInstance;
} | {
  readonly kind: "DONE_FOR_NOW" | "CONTENT_GAP";
  readonly trace: readonly string[];
};
export type LearningNeed = "PROBE" | "SPACED_PRACTICE" | "RETENTION" | "REPAIR";

function tie(seed: string, id: string): number {
  let hash = 2166136261;
  for (const c of `${seed}|${id}`) hash = Math.imul(hash ^ c.charCodeAt(0), 16777619);
  return (hash >>> 0) / 4294967296;
}
function rank(input: PlanningInput, fact: Fact): number {
  return FACT_MASTERY_STAGES.indexOf(input.snapshot.factMasteryById.get(fact.id)?.currentStage ?? "UNSEEN");
}
export function learningNeed(input: PlanningInput, fact: Fact): LearningNeed {
  if (input.scheduling.repairFactIds.includes(fact.id)) return "REPAIR";
  if (input.snapshot.dueFactIds.has(fact.id)) return "RETENTION";
  if (!(input.snapshot.factMasteryById.get(fact.id)?.attemptCount)) return "PROBE";
  return "SPACED_PRACTICE";
}

/** Hard gates first. Scores cannot override instruction, spacing, exposure or dose. */
export function planNext(input: PlanningInput): PlanningResult {
  if (input.targetSkillIds.length === 0) throw new Error("Minimaal één target skill is vereist.");
  const state = input.scheduling;
  const exposedFamilies = new Set(Object.entries(state.facts)
    .filter(([, dose]) => state.independentOrdinal < dose.eligibleAfterOrdinal)
    .map(([id]) => input.catalog.factById.get(id)?.familyId).filter(Boolean));
  const gaps = new Set<string>();
  const candidates = new Map<string, { fact: Fact; target: SkillId }>();
  const lessons: { strategy: StrategyDefinition; target: SkillId; variant: LessonVariant; repair: boolean }[] = [];
  const visited = new Set<SkillId>();
  let blocked = 0;
  const mastered = (id: SkillId) => input.snapshot.masteredSkillIds.has(id);

  const visit = (id: SkillId, repair = false): void => {
    if (visited.has(id)) return;
    visited.add(id);
    const skill = input.skills.find((item) => item.id === id);
    if (!skill) { gaps.add(`unknown skill: ${id}`); return; }
    const missing = skill.prerequisiteIds.filter((key) => !mastered(key));
    if (missing.length) { missing.forEach((key) => visit(key, true)); return; }
    const facts = input.catalog.facts.filter((fact) => fact.skillIds.includes(id));
    const routes = input.strategies.filter((strategy) => strategy.appliesToSkillIds.includes(id));
    if (!facts.length && !routes.length) { gaps.add(`missing concept activity: ${id}`); return; }
    const readyRoutes: StrategyDefinition[] = [];
    for (const strategy of routes) {
      const missingPrereqs = strategy.prerequisiteSkillIds.filter((key) => !mastered(key));
      if (missingPrereqs.length) { missingPrereqs.forEach((key) => visit(key, true)); continue; }
      const mastery = input.snapshot.strategyMasteryById.get(strategy.id);
      const unseen = !mastery || mastery.currentStage === "UNSEEN";
      const repairDue = mastery?.refreshDue || state.repairStrategyIds.includes(strategy.id);
      const available = (state.strategyCounts[strategy.id] ?? 0) < SCHEDULING_POLICY.maximumStrategyActivities;
      if (available && (unseen || repairDue || mastery?.currentStage !== "STABLE")) {
        if (!unseen || (input.session.strategyIntroductionCount < 1 && state.introductions < SCHEDULING_POLICY.maximumNewIntroductions)) {
          const variant: LessonVariant = unseen ? "FULL" : repairDue || mastery?.currentStage === "INTRODUCED" ? "REFRESH" : "CHECK";
          if (!strategy.lesson) gaps.add(`missing strategy lesson: ${strategy.id}`);
          else lessons.push({ strategy, target: id, variant, repair });
        }
      }
      if (!unseen && !repairDue) readyRoutes.push(strategy);
    }
    for (const fact of facts) {
      // A specific taught route must match the operation and actual fact; broad tags are not a bypass.
      const safe = routes.length === 0 ? id === SKILL_IDS.BASIC_TO_10
        : readyRoutes.some((route) => route.supportedOperations.includes(fact.operation) && fact.strategyTags.includes(route.id));
      if (!safe) { blocked += 1; continue; }
      if (factSchedulingBlock(state, fact.id) || input.session.cooldownFactIds.has(fact.id)) {
        blocked += 1;
        if (state.repairFactIds.includes(fact.id) && routes.length === 0) gaps.add(`missing fact repair activity: ${id}`);
        continue;
      }
      if (!candidates.has(fact.id)) candidates.set(fact.id, { fact, target: id });
    }
  };
  input.targetSkillIds.forEach((id) => visit(id));

  // Instruction/repair is bounded and uses an explicit immutable, safe lesson instance.
  for (const choice of lessons) {
    const safeFacts = input.catalog.facts.filter((fact) => !factSchedulingBlock(state, fact.id)
      && !input.session.cooldownFactIds.has(fact.id)
      && (!fact.familyId || !exposedFamilies.has(fact.familyId))
      && fact.skillIds.includes(choice.target));
    // Keep the canonical lookup for metadata about intermediate equations; only candidates are narrowed.
    const catalog = { ...input.catalog, facts: safeFacts };
    let lesson: StrategyLessonInstance;
    try {
      lesson = instantiateStrategyLesson(choice.strategy.id, catalog, input.strategies, input.seed, choice.variant);
    } catch {
      // Distinguish exhausted safe examples from genuinely absent catalog content.
      try { instantiateStrategyLesson(choice.strategy.id, input.catalog, input.strategies, input.seed, choice.variant); }
      catch { gaps.add(`missing safe lesson content: ${choice.strategy.id}`); }
      continue;
    }
    const reason = choice.variant === "CHECK" ? "STRATEGY_INDEPENDENT_CHECK"
      : choice.variant === "REFRESH" ? "STRATEGY_REFRESH"
      : choice.repair ? "PREREQUISITE_REPAIR" : "STRATEGY_INTRODUCTION";
    return { kind: "ITEM", bucket: "CORE", lesson, decision: {
      kind: choice.variant === "CHECK" ? "STRATEGY_CHECK" : choice.variant === "REFRESH" ? "STRATEGY_REFRESH"
        : choice.repair ? "PREREQUISITE_REPAIR" : "STRATEGY_LESSON",
      factId: null, strategyId: choice.strategy.id, targetSkillId: choice.target,
      objective: choice.variant === "FULL" ? "LEARN_STRATEGY" : "PRACTICE_STRATEGY",
      responseMode: "NUMERIC_ENTRY", supportLevel: choice.variant === "CHECK" ? "AVAILABLE" : "FULL",
      selectionReason: reason, seed: input.seed,
      trace: [`policy: ${state.policyVersion}`, `strategy activity: ${choice.variant}`, `reachable skill: ${choice.target}`],
    } };
  }

  let pool = [...candidates.values()];
  if (!pool.length) return { kind: gaps.size ? "CONTENT_GAP" : "DONE_FOR_NOW",
    trace: [`policy: ${state.policyVersion}`, `hard exclusions: ${blocked}`, ...gaps] };
  const due = pool.filter(({ fact }) => input.snapshot.dueFactIds.has(fact.id));
  const consolidation = pool.filter(({ fact }) => rank(input, fact) >= 2);
  let bucket: SessionBucket = input.bucket;
  if (bucket === "MAINTENANCE") {
    if (due.length) pool = due;
    else if (consolidation.length) pool = consolidation;
    else bucket = "CORE";
  }
  if (bucket === "CHALLENGE") {
    // Challenge increases independent application of reachable material, never the number range.
    if (consolidation.length) pool = consolidation;
    else bucket = "CORE";
  }
  const novel = pool.filter(({ fact }) => !(state.lifetimePresentations[fact.id] ?? input.snapshot.factMasteryById.get(fact.id)?.attemptCount));
  const reserveNovel = bucket === "CORE" && state.coreSinceNovel >= SCHEDULING_POLICY.novelCoreEvery - 1 && novel.length > 0;
  const successBalance = !reserveNovel && (input.session.coreItemIndex < 2 || input.session.consecutiveDifficultOutcomes >= 2);
  const strong = pool.filter(({ fact }) => rank(input, fact) >= 2);
  if (reserveNovel) pool = novel;
  else if (successBalance && strong.length) pool = strong;
  const previous = input.catalog.factById.get(state.recentFactIds.at(-1) ?? "");
  const score = (fact: Fact): number => {
    const need = learningNeed(input, fact);
    const lifetime = state.lifetimePresentations[fact.id] ?? input.snapshot.factMasteryById.get(fact.id)?.attemptCount ?? 0;
    // Dose-level fairness before need; small bounded need bonuses cannot starve unseen facts.
    return -(state.facts[fact.id]?.presentations ?? 0) * 100
      + (need === "RETENTION" ? 24 : need === "SPACED_PRACTICE" ? 8 : 4)
      - Math.min(lifetime, 100) * 2
      + (previous?.familyId && fact.familyId === previous.familyId ? -12 : 0)
      + (previous?.operation === fact.operation ? -3 : 0) + tie(input.seed, fact.id);
  };
  pool.sort((a, b) => score(b.fact) - score(a.fact) || a.fact.id.localeCompare(b.fact.id));
  const { fact, target } = pool[0];
  const need = learningNeed(input, fact);
  const retrieval = rank(input, fact) >= 3;
  const reason = successBalance && rank(input, fact) >= 2 ? "SUCCESS_BALANCE"
    : need === "RETENTION" ? "DUE_REVIEW" : need === "PROBE" ? "NEW_SKILL"
    : bucket === "MAINTENANCE" ? "MAINTENANCE" : "SKILL_BUILDING";
  return { kind: "ITEM", bucket, decision: { kind: "PRACTICE", factId: fact.id,
    targetSkillId: target, strategyId: null, selectionReason: reason,
    objective: retrieval ? (need === "RETENTION" || bucket === "MAINTENANCE" ? "MAINTAIN" : "MEMORIZE") : "AUTOMATE",
    responseMode: retrieval ? "DIRECT_RETRIEVAL" : "NUMERIC_ENTRY", supportLevel: retrieval ? "NONE" : "AVAILABLE",
    seed: input.seed, trace: [`policy: ${state.policyVersion}`, `need: ${need}`, `requested bucket: ${input.bucket}`,
      `actual bucket: ${bucket}`, `novel reservation: ${reserveNovel}`, `eligible facts: ${candidates.size}`,
      `hard exclusions: ${blocked}`, `dose before: ${state.facts[fact.id]?.presentations ?? 0}`, ...gaps] } };
}
